import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp, Shield, Globe, BarChart3, Building2, Cpu, ShoppingBag, Heart, HardHat, Sprout, Landmark, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { useContent } from "../contexts/ContentContext";

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const SVC_ICONS = { "Capital Raising": TrendingUp, "Asset Management": BarChart3, "Strategic Advisory": Shield };
const SECTOR_ICONS = { "Manufacturing": Building2, "Technology": Cpu, "Retail & Consumer": ShoppingBag, "Healthcare": Heart, "Infrastructure": HardHat, "Agriculture": Sprout, "Financial Services": Landmark };

const DEFAULT_SECTORS = ["Manufacturing", "Technology", "Retail & Consumer", "Healthcare", "Infrastructure", "Agriculture", "Financial Services"];

export default function HomePage() {
  const { getPage } = useContent();
  const h = getPage("homepage", {});

  const impactNumbers = h.impact_numbers || [{ value: "$2B+", label: "Capital Raised" }, { value: "150+", label: "Transactions" }, { value: "50+", label: "Active Clients" }, { value: "15+", label: "Years Experience" }];
  const services = h.services || [
    { title: "Capital Raising", desc: "Debt advisory, equity fundraising, structured finance, and private placements.", path: "/services/capital-raising" },
    { title: "Asset Management", desc: "Portfolio structuring, wealth advisory, and institutional asset strategies.", path: "/services/asset-management" },
    { title: "Strategic Advisory", desc: "Business expansion, M&A advisory, and financial restructuring.", path: "/services/strategic-advisory" },
  ];
  const differentiators = h.differentiators || [];
  const credibilityLogos = h.credibility_logos || ["HSBC", "Prudential", "Bajaj Finance", "New York Life", "Kotak Mahindra", "Deutsche Bank"];
  const globalLocations = h.global_locations || ["Gurgaon, India (Headquarters)", "Pune, India", "Dubai, UAE (DIFC)"];
  const globalStats = h.global_stats || [{ value: "3", label: "Office Locations" }, { value: "2", label: "Countries" }, { value: "50+", label: "Client Companies" }, { value: "24/7", label: "Client Support" }];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center" style={{ backgroundImage: "url('https://images.pexels.com/photos/34688107/pexels-photo-34688107.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-navy-900/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-40">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.p variants={fadeUp} className="uppercase tracking-[0.25em] text-xs font-semibold text-gold-500 mb-6 font-sans">{h.hero_label || "Capital Advisory & Asset Management"}</motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-white mb-8 leading-[1.1] tracking-tight whitespace-pre-line">{h.hero_headline || "Enabling Growth.\nUnlocking Capital."}</motion.h1>
            <motion.p variants={fadeUp} className="text-white/70 font-sans text-base md:text-lg max-w-xl leading-relaxed mb-10">{h.hero_subtext || ""}</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-cta-button" className="inline-flex items-center gap-2 bg-gold-500 text-navy-900 px-8 py-4 rounded-sm font-sans text-sm font-medium hover:bg-gold-400 transition-colors duration-300">
                {h.hero_cta_text || "Schedule a Consultation"} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/about" data-testid="hero-secondary-cta" className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 rounded-sm font-sans text-sm font-medium hover:bg-white/10 transition-colors duration-300">
                {h.hero_cta_secondary || "Learn More"}
              </Link>
            </motion.div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-navy-950/80 backdrop-blur-sm border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {impactNumbers.map((stat) => (
                <div key={stat.label} className="text-center md:text-left">
                  <p className="font-serif text-3xl md:text-4xl text-gold-500 font-light">{stat.value}</p>
                  <p className="text-white/50 text-xs font-sans uppercase tracking-[0.15em] mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Snapshot */}
      <section data-testid="about-snapshot" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">{h.about_label || "Who We Are"}</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy-900 font-light mb-6 tracking-tight leading-tight">{h.about_headline || "A partner in your growth story."}</h2>
              <p className="text-gray-600 font-sans leading-relaxed mb-6">{h.about_text || ""}</p>
              <p className="text-gray-500 font-sans text-sm leading-relaxed mb-8">{h.about_subtext || ""}</p>
              <Link to="/about" className="inline-flex items-center gap-2 text-navy-900 font-sans text-sm font-medium hover:text-gold-500 transition-colors group">Discover Our Story <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Link>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1759405095276-940d96c915f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwZmluYW5jaWFsJTIwZGlzdHJpY3R8ZW58MHx8fHwxNzc1MzY1Njk3fDA&ixlib=rb-4.1.0&q=85" alt="Modern office" className="w-full aspect-[4/3] object-cover" />
              <div className="absolute -bottom-6 -left-6 bg-navy-900 text-white p-6 max-w-xs">
                <p className="font-serif text-2xl text-gold-500 mb-1">{h.about_stat_value || "15+ Years"}</p>
                <p className="text-white/60 text-sm font-sans">{h.about_stat_label || "Combined leadership experience across global markets"}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-16">
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">{h.services_label || "Our Expertise"}</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-navy-900 font-light tracking-tight">{h.services_headline || "What We Do"}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-gray-200">
            {services.map((svc, i) => {
              const Icon = SVC_ICONS[svc.title] || TrendingUp;
              return (
                <Link key={svc.title} to={svc.path || "/services"} data-testid={`service-card-${i}`} className={`group p-10 bg-white hover:bg-navy-900 transition-all duration-500 ${i < 2 ? 'md:border-r border-b md:border-b-0 border-gray-200' : ''}`}>
                  <Icon className="w-8 h-8 text-gold-500 mb-6 group-hover:text-gold-400 transition-colors" strokeWidth={1.5} />
                  <h3 className="font-serif text-2xl text-navy-900 group-hover:text-white mb-4 transition-colors">{svc.title}</h3>
                  <p className="text-gray-500 group-hover:text-white/60 font-sans text-sm leading-relaxed mb-6 transition-colors">{svc.desc}</p>
                  <span className="inline-flex items-center gap-1 text-gold-500 font-sans text-sm font-medium group-hover:text-gold-400 transition-colors">Learn More <ArrowUpRight className="w-3.5 h-3.5" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section data-testid="sectors-section" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-16">
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">{h.sectors_label || "Industry Focus"}</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-navy-900 font-light tracking-tight">{h.sectors_headline || "Sectors We Serve"}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 border-t border-l border-gray-200">
            {DEFAULT_SECTORS.map(label => {
              const Icon = SECTOR_ICONS[label] || Globe;
              return (
                <Link key={label} to="/sectors" className="group flex flex-col items-center justify-center p-8 md:p-10 border-b border-r border-gray-200 hover:bg-navy-900 transition-all duration-500">
                  <Icon className="w-7 h-7 text-navy-900 group-hover:text-gold-500 mb-4 transition-colors" strokeWidth={1.5} />
                  <span className="text-sm font-sans font-medium text-gray-700 group-hover:text-white text-center transition-colors">{label}</span>
                </Link>
              );
            })}
            <Link to="/sectors" className="group flex flex-col items-center justify-center p-8 md:p-10 border-b border-r border-gray-200 hover:bg-gold-500 transition-all duration-500">
              <Globe className="w-7 h-7 text-gold-500 group-hover:text-navy-900 mb-4 transition-colors" strokeWidth={1.5} />
              <span className="text-sm font-sans font-medium text-gold-500 group-hover:text-navy-900 text-center transition-colors">View All Sectors</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Capstone */}
      <section data-testid="why-capstone-section" className="py-24 md:py-32 relative" style={{ backgroundImage: "url('https://images.pexels.com/photos/5506217/pexels-photo-5506217.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-navy-900/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-16">
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">{h.differentiators_label || "The Capstone Advantage"}</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-white font-light tracking-tight">{h.differentiators_headline || "Why Capstone"}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-white/10">
            {differentiators.map((d, i) => (
              <div key={d.number || i} className={`p-10 ${i < 2 ? 'border-b border-white/10' : ''} ${i % 2 === 0 ? 'md:border-r border-white/10' : ''}`}>
                <span className="text-gold-500 font-sans text-xs tracking-[0.2em]">{d.number}</span>
                <h3 className="font-serif text-2xl text-white mt-3 mb-3">{d.title}</h3>
                <p className="text-white/50 font-sans text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credibility */}
      <section data-testid="credibility-section" className="py-20 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-center uppercase tracking-[0.2em] text-xs font-semibold text-gray-400 mb-10 font-sans">{h.credibility_label || "Our Leaders Have Served At"}</p>
          <div className="flex flex-wrap items-center justify-center gap-12 md:gap-16">
            {credibilityLogos.map(name => (
              <span key={name} className="font-serif text-xl md:text-2xl text-gray-300 hover:text-navy-900 transition-colors duration-300 cursor-default">{name}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section data-testid="global-presence-section" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">{h.global_label || "Global Footprint"}</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy-900 font-light tracking-tight mb-6 leading-tight whitespace-pre-line">{h.global_headline || "Rooted in India.\nConnected Globally."}</h2>
              <p className="text-gray-600 font-sans leading-relaxed mb-8">{h.global_text || ""}</p>
              <div className="space-y-4">
                {globalLocations.map(loc => (
                  <div key={loc} className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-gold-500" /><span className="font-sans text-sm text-gray-700">{loc}</span></div>
                ))}
              </div>
            </div>
            <div className="bg-navy-900 p-8 md:p-12">
              <div className="grid grid-cols-2 gap-8">
                {globalStats.map(s => (
                  <div key={s.label}><p className="font-serif text-4xl text-gold-500 mb-2">{s.value}</p><p className="text-white/50 text-xs font-sans uppercase tracking-[0.15em]">{s.label}</p></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
